import { memo, useEffect, useRef, useState, type RefObject } from 'react';
import { Loader2, Search } from 'lucide-react';
import type { WardrobeItem } from '../../api/virtualUser';
import { categoryRoot, rootCategoryLabel } from '../../lib/wardrobeCategory';
import { startPointerSession } from '../../lib/pointerSession';

type DragPreview = { item: WardrobeItem; x: number; y: number };

export function WardrobeGrid({
  items,
  loading,
  loadingMore,
  hasMore,
  usedIds,
  scrollRoot,
  onLoadMore,
  onDropItem,
  onPickItem,
  pickMode = false,
}: {
  items: WardrobeItem[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  usedIds: Set<string>;
  scrollRoot: RefObject<HTMLDivElement | null>;
  onLoadMore: () => void;
  onDropItem?: (item: WardrobeItem, clientX: number, clientY: number) => void;
  /** Click-to-select without drag-to-canvas (tagging / slot pick). */
  onPickItem?: (item: WardrobeItem) => void;
  pickMode?: boolean;
}) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [preview, setPreview] = useState<DragPreview | null>(null);

  useEffect(() => {
    const el = sentinelRef.current;
    const root = scrollRoot.current;
    if (!el || !root) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) onLoadMore();
      },
      { root, rootMargin: '240px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [onLoadMore, items.length, loading, scrollRoot]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32 text-[var(--muted)]">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        加载衣橱...
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-40 text-[var(--muted)] text-sm">
        <Search className="w-8 h-8 mb-2 opacity-30" />
        没有符合条件的单品
      </div>
    );
  }

  return (
    <>
      <div className="grid gap-1.5 grid-cols-[repeat(auto-fill,minmax(7rem,1fr))]">
        {items.map((item) => (
          <WardrobeCard
            key={item.cloth_id}
            item={item}
            used={usedIds.has(item.cloth_id)}
            pickMode={pickMode}
            onPick={() => onPickItem?.(item)}
            onDragStart={(x, y) => setPreview({ item, x, y })}
            onDragMove={(x, y) => setPreview((p) => (p ? { ...p, x, y } : p))}
            onDragEnd={(x, y) => {
              setPreview(null);
              if (pickMode) {
                onPickItem?.(item);
                return;
              }
              onDropItem?.(item, x, y);
            }}
            onDragCancel={() => setPreview(null)}
          />
        ))}
      </div>
      <div ref={sentinelRef} className="h-8" />
      {loadingMore && (
        <div className="flex items-center justify-center py-3 text-[var(--muted)] text-xs">
          <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
          加载更多...
        </div>
      )}
      {!loadingMore && !hasMore && items.length > 0 && (
        <div className="py-2 text-center text-[10px] text-[var(--muted)]">已加载全部</div>
      )}
      {preview && (
        <div
          className="fixed z-[80] w-28 pointer-events-none opacity-90"
          style={{ left: preview.x - 56, top: preview.y - 56 }}
        >
          <img
            src={preview.item.thumb}
            alt=""
            className="w-full h-auto object-contain drop-shadow-md"
            draggable={false}
          />
        </div>
      )}
    </>
  );
}

const WardrobeCard = memo(function WardrobeCard({
  item,
  used,
  pickMode,
  onPick,
  onDragStart,
  onDragMove,
  onDragEnd,
  onDragCancel,
}: {
  item: WardrobeItem;
  used: boolean;
  pickMode?: boolean;
  onPick?: () => void;
  onDragStart: (x: number, y: number) => void;
  onDragMove: (x: number, y: number) => void;
  onDragEnd: (x: number, y: number) => void;
  onDragCancel: () => void;
}) {
  const [imgError, setImgError] = useState(false);

  return (
    <div
      style={{ userSelect: 'none' }}
      onContextMenu={(e) => e.preventDefault()}
      onClick={() => {
        if (pickMode) onPick?.();
      }}
      onPointerDown={(e) => {
        if (pickMode) return;
        if (e.pointerType === 'mouse' && e.button !== 0 && e.button !== 2) return;
        startPointerSession(e, {
          onDragStart,
          onDragMove,
          onDragEnd,
          onDragCancel,
        });
      }}
      className={`surface p-1 hover:border-[var(--line-strong)] ${
        pickMode ? 'cursor-pointer' : 'cursor-grab active:cursor-grabbing'
      } ${used ? (pickMode ? 'ring-2 ring-[var(--ink)]' : 'opacity-55') : ''}`}
    >
      <div className="media-well aspect-square rounded-md mb-1 bg-[var(--paper-2)]">
        {item.thumb && !imgError ? (
          <img
            src={`${item.thumb}${item.thumb.includes('?') ? '&' : '?'}size=300`}
            alt=""
            draggable={false}
            onError={() => setImgError(true)}
            decoding="async"
          />
        ) : (
          <span className="text-[10px] text-[var(--muted)]">无图</span>
        )}
      </div>
      <div className="text-[11px] leading-tight text-[var(--ink)] truncate" title={item.name}>
        {item.name || item.cloth_id}
      </div>
      <div className="flex items-center justify-between mt-0.5">
        <span className="text-[9px] text-[var(--muted)]">{rootCategoryLabel(categoryRoot(item.category))}</span>
        {used ? (
          <span className="text-[9px] text-[var(--ink)]">画布中</span>
        ) : item.color ? (
          <span className="text-[9px] text-[var(--muted)]">{item.color}</span>
        ) : null}
      </div>
    </div>
  );
});
